function isDialog(element) {
    return typeof HTMLDialogElement !== 'undefined' && element instanceof HTMLDialogElement;
}

function initDialogs(root) {
    const triggers = root.querySelectorAll('[data-dialog-open]');
    if (triggers.length === 0) return;

    triggers.forEach((trigger) => {
        const dialogId = trigger.getAttribute('data-dialog-open');
        const dialog = dialogId ? document.getElementById(dialogId) : null;
        if (!isDialog(dialog)) return;

        let returnFocus = null;
        const close = () => {
            if (dialog.open) dialog.close();
        };

        trigger.addEventListener('click', () => {
            const validationFormId = trigger.getAttribute('data-dialog-validate');
            const validationForm = validationFormId ? document.getElementById(validationFormId) : null;
            if (validationForm instanceof HTMLFormElement && !validationForm.reportValidity()) return;

            returnFocus = trigger;
            if (!dialog.open) dialog.showModal();
        });

        dialog.querySelectorAll('[data-dialog-close]').forEach((button) => {
            button.addEventListener('click', close);
        });

        dialog.addEventListener('click', (event) => {
            if (event.target === dialog) close();
        });

        dialog.addEventListener('close', () => {
            if (returnFocus instanceof HTMLElement) returnFocus.focus();
            returnFocus = null;
        });
    });
}

function initTabs(root) {
    root.querySelectorAll('[data-tabs]').forEach((tabsRoot) => {
        const tabs = Array.from(tabsRoot.querySelectorAll('[role="tab"]'));
        if (tabs.length === 0) return;

        const selectTab = (selectedTab) => {
            tabs.forEach((tab) => {
                const selected = tab === selectedTab;
                const panelId = tab.getAttribute('aria-controls');
                const panel = panelId ? document.getElementById(panelId) : null;

                tab.setAttribute('aria-selected', String(selected));
                tab.tabIndex = selected ? 0 : -1;
                if (panel instanceof HTMLElement) panel.hidden = !selected;
            });
        };

        tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => selectTab(tab));
            tab.addEventListener('keydown', (event) => {
                const supportedKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
                if (!supportedKeys.includes(event.key)) return;

                event.preventDefault();
                let nextIndex = index;
                if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
                if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
                if (event.key === 'Home') nextIndex = 0;
                if (event.key === 'End') nextIndex = tabs.length - 1;

                tabs[nextIndex].focus();
                selectTab(tabs[nextIndex]);
            });
        });

        selectTab(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    });
}

function initMobileNavigation(root) {
    const toggle = root.querySelector('[data-creator-nav-toggle]');
    const menuId = toggle?.getAttribute('aria-controls');
    const menu = menuId ? document.getElementById(menuId) : null;
    if (!(toggle instanceof HTMLButtonElement) || !(menu instanceof HTMLElement)) return;

    const mobileQuery = window.matchMedia?.('(max-width: 767px)');
    const setOpen = (isOpen, restoreFocus = false) => {
        const isMobile = mobileQuery?.matches ?? false;
        if (!isMobile) {
            menu.classList.remove('is-open');
            menu.removeAttribute('aria-hidden');
            toggle.setAttribute('aria-expanded', 'false');
            return;
        }

        menu.classList.toggle('is-open', isOpen);
        menu.setAttribute('aria-hidden', String(!isOpen));
        toggle.setAttribute('aria-expanded', String(isOpen));
        toggle.textContent = isOpen ? 'ポートフォリオメニューを閉じる' : 'ポートフォリオメニュー';
        if (!isOpen && restoreFocus) toggle.focus();
    };

    toggle.addEventListener('click', () => {
        setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });

    root.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || toggle.getAttribute('aria-expanded') !== 'true') return;
        event.preventDefault();
        setOpen(false, true);
    });

    mobileQuery?.addEventListener?.('change', () => setOpen(false));

    setOpen(false);
}

function initStaticForms(root) {
    root.querySelectorAll('[data-static-form]').forEach((form) => {
        if (!(form instanceof HTMLFormElement)) return;

        form.addEventListener('submit', (event) => {
            event.preventDefault();

            let status = form.querySelector('[data-static-form-status]');
            if (!(status instanceof HTMLElement)) {
                status = document.createElement('p');
                status.className = 'creator-form__notice';
                status.dataset.staticFormStatus = '';
                status.setAttribute('role', 'status');
                form.prepend(status);
            }

            status.textContent = 'この機能は準備中のため、操作内容は保存されません。';
        });
    });
}

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

function initProfileAvatarPreview(root) {
    const input = root.querySelector('[data-profile-avatar-input]');
    const preview = root.querySelector('[data-profile-avatar-preview]');
    if (!(input instanceof HTMLInputElement) || !(preview instanceof HTMLElement)) return;

    let objectUrl = null;
    const releaseObjectUrl = () => {
        if (objectUrl === null) return;
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
    };

    input.addEventListener('change', () => {
        const file = input.files?.[0];
        if (!(file instanceof File)) return;

        releaseObjectUrl();
        objectUrl = URL.createObjectURL(file);
        const image = document.createElement('img');
        image.src = objectUrl;
        image.alt = '選択したアイコンのプレビュー';
        preview.replaceChildren(image);
    });

    window.addEventListener('pagehide', releaseObjectUrl, { once: true });
}

function initWorkSubmission(root) {
    const form = root.querySelector('[data-work-submission]');
    if (!(form instanceof HTMLFormElement)) return;

    const endpoint = form.dataset.temporaryUploadUrl;
    const status = form.querySelector('[data-work-submission-status]');
    const errorStatus = form.querySelector('[data-work-submission-error]');
    const dialog = document.getElementById('work-submit');
    const confirm = dialog?.querySelector('[data-work-submit-confirm]');
    let uploading = 0;
    let submitting = false;
    const uploadSequence = { video: 0, thumbnail: 0 };

    const refreshSubmitAvailability = () => {
        const button = form.querySelector('button[type="submit"]');
        if (button instanceof HTMLButtonElement) button.disabled = uploading > 0 || submitting;
        if (confirm instanceof HTMLButtonElement) confirm.disabled = submitting;
    };

    const showStatus = (message = '') => {
        if (!(status instanceof HTMLElement)) return;
        status.textContent = message;
        status.hidden = message === '';
    };
    const showError = (message = '') => {
        if (!(errorStatus instanceof HTMLElement)) return;
        errorStatus.textContent = message;
        errorStatus.hidden = message === '';
        if (message !== '') errorStatus.focus();
    };

    const clearPreview = (kind) => {
        const target = form.querySelector(`[data-temporary-upload-preview="${kind}"]`);
        if (target instanceof HTMLElement) {
            target.replaceChildren();
            target.hidden = true;
        }
        const button = form.querySelector(`[data-temporary-upload-change="${kind}"]`);
        if (button instanceof HTMLElement) button.textContent = kind === 'video' ? '動画を選択する' : 'サムネイルを選択する';
    };

    const preview = (kind, payload) => {
        const target = form.querySelector(`[data-temporary-upload-preview="${kind}"]`);
        if (!(target instanceof HTMLElement)) return;
        target.replaceChildren();
        const name = document.createElement('p');
        name.textContent = `${payload.filename} を一時保存しました。`;
        if (kind === 'video') {
            const video = document.createElement('video');
            video.src = payload.preview_url;
            video.controls = true;
            video.muted = true;
            video.playsInline = true;
            target.append(video);
        } else {
            const image = document.createElement('img');
            image.src = payload.preview_url;
            image.alt = '選択したサムネイルのプレビュー';
            target.append(image);
        }
        target.append(name);
        target.hidden = false;
        const button = form.querySelector(`[data-temporary-upload-change="${kind}"]`);
        if (button instanceof HTMLElement) button.textContent = kind === 'video' ? '動画を変更する' : 'サムネイルを変更する';
    };

    const upload = async (kind, file) => {
        if (!endpoint || !(file instanceof File)) return;
        const id = form.querySelector(`[data-temporary-upload-id="${kind}"]`);
        if (!(id instanceof HTMLInputElement)) return;

        const requestSequence = ++uploadSequence[kind];
        const previousTemporaryUploadId = id.value;
        id.value = '';
        clearPreview(kind);
        showError('');
        uploading += 1;
        refreshSubmitAvailability();
        showStatus(`${kind === 'video' ? '動画' : 'サムネイル'}を一時保存しています。`);
        try {
            const body = new FormData();
            body.set('kind', kind);
            body.set('file', file);
            if (previousTemporaryUploadId !== '') body.set('replaces_temporary_upload_id', previousTemporaryUploadId);
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrfToken() },
                credentials: 'same-origin',
                body,
            });
            const json = await response.json().catch(() => ({}));
            if (!response.ok || !json?.data?.id) {
                throw new Error(json?.message || '一時保存に失敗しました。');
            }
            if (requestSequence !== uploadSequence[kind]) return;
            id.value = json.data.id;
            preview(kind, json.data);
            showStatus('');
        } catch (error) {
            if (requestSequence !== uploadSequence[kind]) return;
            id.value = '';
            clearPreview(kind);
            showError(error instanceof Error ? error.message : '一時保存に失敗しました。');
        } finally {
            uploading -= 1;
            refreshSubmitAvailability();
        }
    };

    ['video', 'thumbnail'].forEach((kind) => {
        const input = form.querySelector(`[data-temporary-upload-input="${kind}"]`);
        const zone = form.querySelector(`[data-temporary-upload-dropzone="${kind}"]`);
        if (input instanceof HTMLInputElement) {
            input.addEventListener('change', () => {
                const file = input.files?.[0];
                input.value = '';
                upload(kind, file);
            });
        }
        if (!(zone instanceof HTMLElement)) return;
        ['dragenter', 'dragover'].forEach((eventName) => zone.addEventListener(eventName, (event) => {
            event.preventDefault();
            zone.classList.add('is-drag-over');
        }));
        ['dragleave', 'drop'].forEach((eventName) => zone.addEventListener(eventName, (event) => {
            event.preventDefault();
            zone.classList.remove('is-drag-over');
        }));
        zone.addEventListener('drop', (event) => upload(kind, event.dataTransfer?.files?.[0]));
    });

    const hasTemporaryFiles = () => ['video', 'thumbnail'].every((kind) => {
        const input = form.querySelector(`[data-temporary-upload-id="${kind}"]`);
        return input instanceof HTMLInputElement && input.value !== '';
    });

    const submit = async () => {
        if (submitting) return;
        if (uploading > 0) {
            showError('ファイルの一時保存が完了してから投稿してください。');
            return;
        }
        if (!hasTemporaryFiles()) {
            showError('動画とサムネイルを選択し、一時保存を完了してください。');
            return;
        }
        submitting = true;
        if (isDialog(dialog) && dialog.open) dialog.close();
        refreshSubmitAvailability();
        showStatus('投稿を保存しています。');
        try {
            const response = await fetch(form.action, {
                method: 'POST',
                headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrfToken() },
                credentials: 'same-origin',
                body: new FormData(form),
            });
            const json = await response.json().catch(() => ({}));
            if (!response.ok) {
                const firstError = Object.values(json?.errors || {}).flat()[0];
                throw new Error(firstError || json?.message || '投稿を保存できませんでした。');
            }
            window.location.assign(json?.data?.redirect_url || '/creator/videos');
        } catch (error) {
            showError(error instanceof Error ? error.message : '投稿を保存できませんでした。');
        } finally {
            submitting = false;
            refreshSubmitAvailability();
        }
    };

    form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        if (uploading > 0 || !hasTemporaryFiles()) {
            if (!hasTemporaryFiles()) showError('動画とサムネイルを選択し、一時保存を完了してください。');
            return;
        }
        if (isDialog(dialog) && !dialog.open) dialog.showModal();
    });
    if (confirm instanceof HTMLButtonElement) {
        confirm.addEventListener('click', () => submit());
    }
    if (isDialog(dialog)) {
        dialog.querySelectorAll('[data-dialog-close]').forEach((button) => {
            button.addEventListener('click', () => dialog.close());
        });
        dialog.addEventListener('click', (event) => {
            if (event.target === dialog) dialog.close();
        });
    }
}

export function initCreatorPortal() {
    if (typeof document === 'undefined') return;

    document.querySelectorAll('[data-creator-portal]').forEach((root) => {
        initDialogs(root);
        initTabs(root);
        initMobileNavigation(root);
        initStaticForms(root);
        initProfileAvatarPreview(root);
        initWorkSubmission(root);
    });
}
